import {
  ADMIN_ORDER_DETAILS,
  AiCallIconTooltip,
  Box,
  centsToDollars,
  CheckmarkBlue,
  Disclaimer,
  Div,
  formatDateTimeShort,
  formatter,
  H5,
  Icon,
  Img,
  InfoComponentMobile,
  isAdmin,
  lightTheme,
  ManualIconTooltip,
  OrderType,
  Provider,
  renderProviderImage,
  renderFleetIcon,
  ScheduledIconTooltip,
  SimpleOrderEntity,
  StatusMark,
  TooltipIcon,
  TooltipWrapper,
  TruncatedText,
  VDriveCancelledIcon,
  VDriveOrderStatus,
  VDrivePendingIcon,
  VDriveSuccessIcon,
  VDriveTruckIcon,
  VerticalSeparator,
} from '@vgrubs/components';
import { PosIconTooltip } from 'components/src/shared/PosIconTooltip';
import { memo } from 'react';
import { useNavigate } from 'react-router';
import { RecoveryStatusBadge } from './RecoveryStatusBadge';
import { RecoveryCell } from './OrdersStyled';
import { FoodRatingTag } from './components/FoodRatingTag';
import { OrdersStatusPill, RenderStatus } from './components/OrdersStatusPill';

const orderType = {
  DELIVERY: 'delivery',
  PICKUP: 'pickup',
  DINEIN: 'dinein',
};

const orderStatus = {
  NEW: 'new',
  ACCEPTED: 'accepted',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
};

const renderOrderType = (t: OrderType) => {
  switch (t) {
    case orderType.DELIVERY:
      return 'Delivery';
    case orderType.PICKUP:
      return 'Pickup';
    case orderType.DINEIN:
      return 'Dine in';

    default:
      break;
  }
};

const renderOrderStatusBorder = (order: SimpleOrderEntity) => {
  if (order.status === orderStatus.ACCEPTED) {
    return lightTheme.green;
  }
  if (order.status === orderStatus.NEW) {
    return lightTheme.yellow;
  }
  if (order.status === orderStatus.COMPLETED) {
    return lightTheme.green;
  }
  if (order.status === orderStatus.CANCELLED) {
    return lightTheme.primary;
  }
  return '';
};

const OrderComponentV2Inner = ({
  data,
  onBeforeNavigate,
}: {
  data: SimpleOrderEntity;
  index: number;
  onBeforeNavigate?: () => void;
}) => {
  const navigate = useNavigate();
  const borderColor = renderOrderStatusBorder(data);
  const isRestaurant = data.deliveryType === 'restaurant';
  const isManual = data?.creationType === 'manual';
  const hasAiCall =
    data?.vDrive?.interceptorCallStatus !== null &&
    data?.vDrive?.interceptorCallStatus !== undefined;
  const isScheduled = data?.isScheduled;
  const isPos = data?.posPartnerId !== null;

  const sideIcons = (
    <Div
      $flex
      $column
      $alignCenter
      $justifyCenter
      $absolute
      style={{
        left: '0',
        top: '50%',
        transform: 'translate(-50%, -50%)',
      }}
      $gap="4px"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      {isManual && <ManualIconTooltip tooltip="Manual order" />}

      {hasAiCall && (
        <AiCallIconTooltip
          resultFailed={data?.vDrive?.interceptorCallResultFailed ?? false}
        />
      )}

      {isScheduled && (
        <ScheduledIconTooltip
          scheduledFor={data.scheduledFor ?? ''}
          timeZone={data.timeZone ?? ''}
        />
      )}

      {isPos && (
        <PosIconTooltip
          id={data.id}
          posType={data.posType ?? undefined}
          posInjectionStatus={data.posInjectionStatus}
        />
      )}
    </Div>
  );

  const locationName = data.storeName;
  const brandName = data.brandName;

  return (
    <>
      <Box
        onClick={() => {
          onBeforeNavigate?.();
          navigate(`${ADMIN_ORDER_DETAILS.replace(':id', data.id.toString())}`);
        }}
        style={{
          boxShadow: 'none',
          border: borderColor && `1px solid ${borderColor}`,
        }}
        $mb="8"
        $flex
        $alignCenter
        $gap="12px"
        $relative
        $pointer
        $desktop
        $height="64px"
        $p="0px 20px"
      >
        {isAdmin && !isManual && !hasAiCall && !isScheduled && !isPos && (
          <StatusMark $color={borderColor} />
        )}

        {!isAdmin && <StatusMark $color={borderColor} />}

        {isAdmin && sideIcons}

        <Div $width={'5%'}>
          <TooltipWrapper
            tooltip={data.id.toString()}
            copy={data.id.toString()}
          >
            <Disclaimer $m="0">
              <TruncatedText
                underline={data.id && data.id.toString().length ? true : false}
              >
                {data.id && data.id.toString().length
                  ? data.id.toString()
                  : '-'}
              </TruncatedText>
            </Disclaimer>
          </TooltipWrapper>
        </Div>

        <Div $width={'5%'} $minWidth="0">
          <TooltipWrapper tooltip={data.number} copy={data.number}>
            <Disclaimer $m="0">
              <TruncatedText
                underline={data.number && data.number.length ? true : false}
              >
                {data.number && data.number.length ? data.number : '-'}
              </TruncatedText>
            </Disclaimer>
          </TooltipWrapper>
        </Div>

        <Div $width={'11%'} $minWidth="0">
          <TooltipWrapper
            tooltip={`${formatDateTimeShort(
              data.date,
              data.timeZone
            )} <br/> Timezone: ${data?.timeZone}`}
          >
            <Disclaimer $m="0">
              {formatDateTimeShort(data.date, data.timeZone)}
            </Disclaimer>
          </TooltipWrapper>
        </Div>

        <Div $width={'18%'} $minWidth="0">
          <TooltipWrapper
            tooltip={`<b>Location:</b> ${data.storeName} <br/> ${
              !data.isFromLocation
                ? `<b>Brand:</b> <span style="color:#E61E29">${data.brandName}</span>`
                : ''
            }`}
          >
            <Disclaimer
              $m="0"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/locations/${data.locationId}`);
              }}
            >
              <TruncatedText underline>{locationName}</TruncatedText>
            </Disclaimer>
            {!data.isFromLocation && (
              <Disclaimer
                $m="0"
                onClick={(e) => {
                  e.stopPropagation();
                  isAdmin && navigate(`/brand/${data.brandId}`);
                }}
              >
                <TruncatedText color={lightTheme.primary} underline>
                  {brandName}
                </TruncatedText>
              </Disclaimer>
            )}
          </TooltipWrapper>
        </Div>

        <Div $width={'10%'} $minWidth="0">
          <Div $flex $alignCenter $gap="4px">
            {/* Shrinks so the opted-in mark beside a long name still fits. */}
            <TooltipWrapper
              tooltip={data.customerName ?? undefined}
              copy={data.customerName ?? undefined}
              style={{ minWidth: 0 }}
            >
              <Disclaimer $m="0">
                <TruncatedText
                  underline={
                    data.customerName && data.customerName.length > 0
                      ? true
                      : false
                  }
                >
                  {data.customerName && data.customerName.length > 0
                    ? data.customerName
                    : '-'}
                </TruncatedText>
              </Disclaimer>
            </TooltipWrapper>
            {data.vDrive && data.vDrive.specialOfferPhone && (
              <TooltipWrapper
                tooltip={`Opted in number: ${
                  data.vDrive.specialOfferPhone.isUSNumber
                    ? data.vDrive.specialOfferPhone.nationalNumber
                    : data.vDrive.specialOfferPhone.number
                }`}
              >
                <Icon $size={12}>
                  <CheckmarkBlue />
                </Icon>
              </TooltipWrapper>
            )}
          </Div>
          <FoodRatingTag rating={data.foodRating} starSize={12} />
        </Div>

        <Div $width={'6%'} $minWidth="0">
          {isRestaurant &&
            data.type !== OrderType.pickup &&
            data.vDriveProvider === null && (
              <Disclaimer $m="0" $color={lightTheme.primary}>
                Self Delivery
              </Disclaimer>
            )}

          {isRestaurant && data.vDriveProvider !== null && (
            <Disclaimer $m="0">vDrive</Disclaimer>
          )}

          {!isRestaurant && (
            <Disclaimer $m="0">{renderOrderType(data.type)}</Disclaimer>
          )}
        </Div>

        <Div $width={'8%'} $minWidth="0">
          {data.provider && data.provider !== ('unknown' as Provider) ? (
            <Div
              $background={lightTheme.white}
              $borderRadius="8px"
              $flex
              $center
              $border="1px solid #e3e3e3"
              $height="44px"
            >
              <Img
                alt=""
                src={renderProviderImage(data.provider, true)}
                $height="44px"
              />
            </Div>
          ) : (
            <Disclaimer $m="0">-</Disclaimer>
          )}
        </Div>

        <Div $width={'9%'} $minWidth="0">
          {' '}
          {data.deliveryType === 'restaurant' &&
          data.vDriveProvider !== null ? (
            <Div
              $flex
              $center
              $border="1px solid #e3e3e3"
              $background={lightTheme.white}
              $borderRadius="8px"
              $height="44px"
              $relative
            >
              <Div
                $flex
                $alignStart
                style={{ justifyContent: 'space-around' }}
                $mt="4"
                $width="calc(100% - 8px)"
              >
                <Icon $size={32}>
                  <VDriveIconStatus status={data.vDrive?.status ?? null} />
                </Icon>

                <VerticalSeparator
                  $h="32px"
                  $w="1px"
                  $color={lightTheme.mediumGray}
                  $absolute
                  $left="50%"
                  $top="50%"
                  $transform="translate(-50%, -50%)"
                />

                <Div $ml="2">
                  <Img
                    alt=""
                    src={renderFleetIcon(data.vDriveProvider ?? null)}
                    $height="28px"
                  />
                </Div>
              </Div>
            </Div>
          ) : (
            <Disclaimer $m="0">-</Disclaimer>
          )}
        </Div>

        <Div $width={'10%'} $minWidth="0">
          <Div $flex $alignCenter $gap="4px">
            <OrdersStatusPill
              status={data?.status}
              rejectReason={data?.rejectReason ?? ''}
            />
          </Div>
        </Div>

        <RecoveryCell>
          {data?.recovery ? (
            <RecoveryStatusBadge recovery={data?.recovery} compact />
          ) : (
            <Disclaimer $m="0">-</Disclaimer>
          )}
        </RecoveryCell>

        <Div $width={'6%'} $minWidth="0">
          <Disclaimer $m="0">
            {formatter.format(centsToDollars(data.subtotalPrice ?? 0))}
          </Disclaimer>
        </Div>
      </Box>
      <Box
        onClick={() => {
          onBeforeNavigate?.();
          navigate(`${ADMIN_ORDER_DETAILS.replace(':id', data.id.toString())}`);
        }}
        style={{
          boxShadow: 'none',
          border: borderColor && `1px solid ${borderColor}`,
        }}
        $mb="8"
        $flex
        $column
        $gap="4px"
        $relative
        $pointer
        $mobile
        $p="12px 24px"
        $pSM="16px 20px"
        $heightSM="196px"
      >
        {isAdmin && !isManual && !hasAiCall && !isScheduled && !isPos && (
          <StatusMark $color={borderColor} />
        )}
        {!isAdmin && <StatusMark $color={borderColor} />}
        {isAdmin && sideIcons}
        <Div>
          <InfoComponentMobile
            component={
              /* Clear of the provider logos in the top right corner; the
                 name shrinks so the rating right after it still fits. */
              <Div $flex $alignCenter $gap="6px" $width="calc(100% - 110px)">
                <Div $minWidth="0">
                  <H5 $m="0">
                    <TruncatedText>{data?.customerName} </TruncatedText>
                  </H5>
                </Div>

                <FoodRatingTag rating={data.foodRating} besideName />
                {data.vDrive && data.vDrive.specialOfferPhone && (
                  <TooltipWrapper
                    tooltip={`Opted in number: ${
                      data.vDrive.specialOfferPhone.isUSNumber
                        ? data.vDrive.specialOfferPhone.nationalNumber
                        : data.vDrive.specialOfferPhone.number
                    }`}
                  >
                    <Icon $size={16}>
                      <CheckmarkBlue />
                    </Icon>
                  </TooltipWrapper>
                )}
              </Div>
            }
          />
        </Div>
        <InfoComponentMobile
          title="Order ID"
          component={
            <TooltipWrapper
              tooltip={
                data.id && data.id.toString().length
                  ? data.id.toString()
                  : undefined
              }
              copy={data.id.toString()}
            >
              <Disclaimer $m="0">{data.id ?? '-'}</Disclaimer>
            </TooltipWrapper>
          }
        />
        <InfoComponentMobile
          title="Number"
          component={
            <TooltipWrapper
              tooltip={
                data.number && data.number.length ? data.number : undefined
              }
              copy={data.number}
            >
              <Disclaimer $m="0">
                {data.number && data.number.length ? data.number : '-'}
              </Disclaimer>
            </TooltipWrapper>
          }
        />
        <Div $minWidth="0">
          <InfoComponentMobile
            title="Date & Time"
            component={
              <TooltipWrapper
                $maxWidth="calc(100% - 150px)"
                tooltip={`${formatDateTimeShort(
                  data.date,
                  data.timeZone
                )} <br/> Timezone: ${data?.timeZone}`}
              >
                <Disclaimer $m="0">
                  <TruncatedText>
                    {formatDateTimeShort(data.date, data.timeZone)}
                  </TruncatedText>
                </Disclaimer>
              </TooltipWrapper>
            }
          />
        </Div>
        <Div $minWidth="0">
          <InfoComponentMobile
            title="Store"
            component={
              <TooltipWrapper
                $maxWidth="calc(100% - 110px)"
                tooltip={`<b>Location:</b> ${data.storeName} <br/> ${
                  !data.isFromLocation
                    ? `<b>Brand:</b> <span style="color:#E61E29">${data.brandName}</span>`
                    : ''
                }`}
              >
                <Disclaimer
                  $m="0"
                  $underline
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/locations/${data.locationId}`);
                  }}
                >
                  <TruncatedText>
                    {data.storeName}{' '}
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        isAdmin && navigate(`/brand/${data.brandId}`);
                      }}
                      style={{
                        color: lightTheme.primary,
                      }}
                    >
                      {!data.isFromLocation && ` / ${data.brandName}`}
                    </span>
                  </TruncatedText>
                </Disclaimer>
              </TooltipWrapper>
            }
          />
        </Div>
        <InfoComponentMobile
          title="Order Type"
          component={
            <>
              {isRestaurant &&
                data.type !== OrderType.pickup &&
                data.vDriveProvider === null && (
                  <Disclaimer $m="0" $color={lightTheme.primary}>
                    Self Delivery
                  </Disclaimer>
                )}
              {isRestaurant && data.vDriveProvider !== null && (
                <Disclaimer $m="0">vDrive</Disclaimer>
              )}
              {!isRestaurant && (
                <Disclaimer $m="0">{renderOrderType(data.type)}</Disclaimer>
              )}
            </>
          }
        />{' '}
        <InfoComponentMobile
          title="Order Status"
          component={
            <Div $flex $alignCenter $gap="4px">
              <Disclaimer $m="0" $color={RenderStatus(data?.status)?.color}>
                {RenderStatus(data?.status)?.name}
              </Disclaimer>

              {data?.rejectReason && (
                <TooltipIcon tooltip={data?.rejectReason} />
              )}
            </Div>
          }
        />
        <Div
          $absolute
          $right="20px"
          $top="16px"
          $flex
          $column
          $alignEnd
          $gap="4px"
          $betweenSM
        >
          <Div $flex $column $gap="4px">
            {data.provider && data.provider !== ('unknown' as Provider) ? (
              <Div
                $borderRadius="8px"
                $flex
                $center
                $border="1px solid #e3e3e3"
                $height="40px"
                $p="0px 4px"
                $width="80px"
              >
                <Img
                  alt=""
                  src={renderProviderImage(data.provider, true)}
                  $heightSM="44px"
                />
              </Div>
            ) : null}

            {data.deliveryType === 'restaurant' &&
            data.vDriveProvider !== null ? (
              <Div
                $flex
                $center
                $border="1px solid #e3e3e3"
                $background={lightTheme.white}
                $borderRadius="8px"
                $heightSM="44px"
                $relative
              >
                <Div
                  $flex
                  $alignStart
                  style={{ justifyContent: 'space-around' }}
                  $mt="4"
                  $width="calc(100% - 8px)"
                >
                  <Icon $size={32}>
                    <VDriveIconStatus status={data.vDrive?.status ?? null} />
                  </Icon>

                  <VerticalSeparator
                    $h="32px"
                    $w="1px"
                    $color={lightTheme.mediumGray}
                    $absolute
                    $left="50%"
                    $top="50%"
                    $transform="translate(-50%, -50%)"
                  />
                  <Div $ml="2">
                    <Img
                      alt=""
                      src={renderFleetIcon(data.vDriveProvider ?? null)}
                      $height="28px"
                    />
                  </Div>
                </Div>
              </Div>
            ) : null}
          </Div>
        </Div>
        <Div $absolute $right="20px" $bottom="16px" $maxWidth="130px">
          {data?.recovery && <RecoveryStatusBadge recovery={data?.recovery} />}
        </Div>
        <InfoComponentMobile
          title="Subtotal"
          component={
            <Disclaimer $m="0">
              {formatter.format(centsToDollars(data.subtotalPrice ?? 0))}
            </Disclaimer>
          }
        />
      </Box>
    </>
  );
};

const VDriveIconStatus = ({ status }: { status: VDriveOrderStatus | null }) => {
  switch (status) {
    case 'completed':
      return <VDriveSuccessIcon />;

    case 'assigned':
    case 'notAssigned':
    case 'canBeCancelled':
      return <VDrivePendingIcon />;

    case 'cancelled':
      return <VDriveCancelledIcon />;

    default:
      return <VDriveTruckIcon />;
  }
};

export const OrderComponentV2 = memo(OrderComponentV2Inner);
